import type { FundingLoanRateOverride } from "../domain/fundingProject";
import { isFundingDate } from "./companyFundingValidation";

interface RateChange { date: string; ratePctX100: number }
interface RateFacility { id: string; name: string; interestRatePctX100: number | null; rateChanges?: readonly RateChange[] }
const validRate=(value:unknown):value is number=>typeof value==="number"&&Number.isSafeInteger(value)&&value>=0&&value<=10000;

/**
 * Applies named stress assumptions to a planning copy only. Pass the full source
 * set for identity validation, or prefilter validated overrides to a project subset.
 * A future scenario rate never supplies a missing historical baseline rate.
 */
export function applyFundingRateAssumptions<T extends RateFacility>(facilities:readonly T[],overrides:readonly FundingLoanRateOverride[],asOfDate:string){
  const assumptions:string[]=[],issues:string[]=[];
  const copies:Array<Omit<T,"rateChanges">&{rateChanges:RateChange[]}>=facilities.map(facility=>({...facility,rateChanges:(facility.rateChanges??[]).map(change=>({...change}))}));
  if(!isFundingDate(asOfDate))issues.push("Choose a real baseline before applying scenario interest rates.");
  if(facilities.length>500||overrides.length>500)issues.push("Scenario loan-rate inputs exceed bounded planning limits.");
  const identities=new Set(facilities.map(facility=>facility.id));
  if(identities.size!==facilities.length)issues.push("Scenario loan-rate sources must have unique facility identities.");
  const scopes=new Set<string>();
  for(const override of overrides){
    const scope=`${override.facilityId}:${override.date}`;
    if(scopes.has(scope))issues.push("Use one scenario interest rate per facility and effective date.");scopes.add(scope);
    if(!identities.has(override.facilityId))issues.push("A scenario interest rate references an unavailable facility.");
    if(!isFundingDate(override.date)||!isFundingDate(asOfDate)||override.date<asOfDate||Date.parse(override.date)-Date.parse(asOfDate)>3660*86400000)issues.push("Scenario interest rates cannot rewrite the past or exceed the bounded planning horizon.");
    if(!validRate(override.ratePctX100))issues.push("Scenario interest rates require whole basis points between zero and 100 percent.");
  }
  for(const facility of facilities){
    const dates=new Set<string>();
    for(const change of facility.rateChanges??[]){
      if(!isFundingDate(change.date)||!validRate(change.ratePctX100)||dates.has(change.date))issues.push(`${facility.name}: source rate history needs review before scenario rates can be applied.`);
      dates.add(change.date);
    }
  }
  if(issues.length)return{facilities:copies,assumptions,issues:[...new Set(issues)]};
  for(const copy of copies){
    const applicable=overrides.filter(override=>override.facilityId===copy.id).sort((a,b)=>a.date.localeCompare(b.date));
    if(!applicable.length)continue;
    const merged=new Map(copy.rateChanges.map(change=>[change.date,change]));
    for(const override of applicable){
      merged.set(override.date,{date:override.date,ratePctX100:override.ratePctX100});
      assumptions.push(`${copy.name}: scenario-only APR ${Math.floor(override.ratePctX100/100)}.${String(override.ratePctX100%100).padStart(2,"0")}% from ${override.date}; recorded loan terms remain unchanged.`);
    }
    copy.rateChanges=[...merged.values()].sort((a,b)=>a.date.localeCompare(b.date));
    if(copy.interestRatePctX100===null)issues.push(`${copy.name}: the baseline interest rate remains unknown; a future scenario assumption does not establish earlier interest.`);
  }
  return{facilities:copies,assumptions,issues};
}
